/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, absoluteApiFilePreviewUrl, getApiErrorMessage } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'
import OrgMultiSelect from './OrgMultiSelect'
import { useAuth } from '../auth/AuthContext'
import { Permissions, Roles, hasAnyPermission as userHasAnyPermission } from '../auth/rbac'
import OrgLandingPageViewer from './OrgLandingPageViewer'

export default function OrgDetail({ selectedOrg, onUpdate, onDelete }) {
    const { show } = useToast()
    const { user } = useAuth()
    const [orgDetail, setOrgDetail] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // 폼 상태
    const [editName, setEditName] = useState('')
    const [editServiceProfile, setEditServiceProfile] = useState('FULL_SERVICE')
    const [editParent, setEditParent] = useState('')
    const [parentOptions, setParentOptions] = useState([])
    const [moveLoading, setMoveLoading] = useState(false)
    const [claimLogoBusy, setClaimLogoBusy] = useState(false)

    // 매핑 상태
    const [mappings, setMappings] = useState({ type: '', gas: [], insurers: [] })
    const [candidateIds, setCandidateIds] = useState([]) // 원수사 기준 GA 다중 선택용

    // 원수사 선택용 조직 옵션(트리 탐색/표시용)
    const [orgOptions, setOrgOptions] = useState([])

    // 보험사(회사) 마스터 목록
    const [insurerMasters, setInsurerMasters] = useState([])

    // GA 매핑 편집 팝업 상태
    const [editInsurerMaster, setEditInsurerMaster] = useState(null) // {id, name, orgId...}
    const [editL1, setEditL1] = useState('')
    const [editL2, setEditL2] = useState('')
    const [editSaving, setEditSaving] = useState(false)

    // 탭 상태
    const [activeTab, setActiveTab] = useState('info')

    // 조직 상세 정보 로딩
    useEffect(() => {
        if (!selectedOrg) {
            setOrgDetail(null)
            return
        }

        let ignore = false
        async function loadDetail() {
            setLoading(true)
            setError(null)
            try {
                const res = await api.get(`/orgs/${selectedOrg.id}`)
                if (!ignore) {
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    setOrgDetail(payload)
                    setEditName(payload?.name || '')
                    setEditServiceProfile(payload?.serviceProfile || 'FULL_SERVICE')
                    setEditParent(payload?.parentOrgId || '')
                }
            } catch (e) {
                if (!ignore) setError('조직 정보를 불러오지 못했습니다')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        loadDetail()
        return () => { ignore = true }
    }, [selectedOrg])

    // 부모 조직 옵션 로딩
    useEffect(() => {
        if (!selectedOrg) return

        let ignore = false
        async function loadParents() {
            try {
                const res = await api.get('/orgs/parent-options', { params: { excludeId: selectedOrg.id } })
                if (!ignore) {
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    setParentOptions(Array.isArray(payload) ? payload : [])
                }
            } catch (e) {
                console.error('Failed to load parent options:', e)
            }
        }
        loadParents()
        return () => { ignore = true }
    }, [selectedOrg])

    // 매핑용 전체 조직 옵션 로딩 (운영자만 접근 가능한 화면이므로 전체 로딩)
    useEffect(() => {
        let ignore = false
        async function loadOrgOptions() {
            try {
                const res = await api.get('/orgs/options')
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setOrgOptions(Array.isArray(payload) ? payload : [])
            } catch {
                if (!ignore) setOrgOptions([])
            }
        }
        loadOrgOptions()
        return () => { ignore = true }
    }, [])

    // 보험사(회사) 마스터 로딩: 생보/손보 전체 리스트를 화면에 노출하기 위함
    useEffect(() => {
        let ignore = false
        async function loadInsurers() {
            try {
                const res = await api.get('/insurers')
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const items = payload?.items
                if (!ignore) setInsurerMasters(Array.isArray(items) ? items : [])
            } catch (_) {
                if (!ignore) setInsurerMasters([])
            }
        }
        loadInsurers()
        return () => { ignore = true }
    }, [])

    // 매핑 로드
    const reloadMappings = useCallback(async () => {
        if (!selectedOrg) return
        try {
            const res = await api.get(`/orgs/${selectedOrg.id}/mappings`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            setMappings(payload || { type: '', gas: [], insurers: [] })
        } catch (_) {
            setMappings({ type: '', gas: [], insurers: [] })
        }
    }, [selectedOrg])

    useEffect(() => {
        reloadMappings()
    }, [reloadMappings])

    const handleMove = async (e) => {
        e.preventDefault()
        if (!selectedOrg) return

        setMoveLoading(true)
        try {
            const newParentId = editParent ? Number(editParent) : null
            await api.patch(`/orgs/${selectedOrg.id}/move`, { newParentId })
            show('조직 이동 완료', 'success')
            onUpdate?.()
        } catch (e) {
            show('조직 이동 실패', 'error')
        } finally {
            setMoveLoading(false)
        }
    }

    const handleUpdate = async (e) => {
        e.preventDefault()
        if (!selectedOrg) return

        try {
            const payload = { name: editName }
            if (isOperator) {
                payload.serviceProfile = editServiceProfile
            }
            await api.patch(`/orgs/${selectedOrg.id}`, payload)
            show('조직 수정 완료', 'success')
            onUpdate?.()
        } catch (e) {
            show('조직 수정 실패', 'error')
        }
    }

    const handleDelete = async (e) => {
        e.preventDefault()
        if (!selectedOrg) return

        const childrenCount = Number(orgDetail?.childrenCount ?? orgDetail?.childCount ?? 0)
        if (childrenCount > 0) {
            show('하위 조직이 있어 삭제할 수 없습니다', 'error')
            return
        }
        const userCount = Number(orgDetail?.userCount ?? 0)
        if (userCount > 0) {
            show('해당 조직에 구성원이 있어 삭제할 수 없습니다.', 'error')
            return
        }

        if (!window.confirm('정말 삭제하시겠습니까?')) return

        try {
            await api.delete(`/orgs/${selectedOrg.id}`)
            show('조직 삭제 완료', 'success')
            onDelete?.()
        } catch (e) {
            show(getApiErrorMessage(e, '조직 삭제 실패'), 'error')
        }
    }

    const onAddMapping = async () => {
        if (!selectedOrg) return

        const isGAOrg = orgDetail?.type === 'GA'
        const isInsurerOrg = orgDetail?.type === 'INSURER'

        try {
            if (isGAOrg) {
                // GA 조직의 원수사 매핑은 "보험사(회사) 전체 리스트"에서 개별 {수정}로 관리
                show('원수사 매핑은 목록에서 보험사별 {수정} 버튼으로 설정하세요.', 'info', 500)
            } else if (isInsurerOrg) {
                // 원수사 조직에서 GA 매핑 추가: 기존 다중 선택 유지
                if (candidateIds.length === 0) return
                for (const id of candidateIds) {
                    await api.post(`/orgs/${selectedOrg.id}/mappings`, { counterpartOrgId: id })
                }
                show('매핑이 추가되었습니다', 'success', 500)
                setCandidateIds([])
            } else {
                // 그 외 타입은 아직 매핑 대상 아님
                show('매핑 가능한 조직 유형이 아닙니다.', 'error', 500)
                return
            }
            await reloadMappings()
        } catch (e) {
            show('매핑 추가 실패', 'error')
        }
    }

    const onRemoveMapping = async (counterpartOrgId) => {
        if (!selectedOrg) return
        try {
            await api.delete(`/orgs/${selectedOrg.id}/mappings`, { params: { counterpartOrgId } })
            show('매핑이 삭제되었습니다', 'success', 1000) // 1초 표시
            await reloadMappings()
        } catch (e) {
            show('매핑 삭제 실패', 'error')
        }
    }

    const onToggleInherit = async (enabled) => {
        if (!selectedOrg) return
        try {
            await api.patch(`/orgs/${selectedOrg.id}/mappings/inherit`, { enabled: !!enabled })
            show(enabled ? '상위 매핑 사용이 활성화되었습니다' : '상위 매핑 사용이 해제되었습니다', 'success', 500)
            await reloadMappings()
        } catch (e) {
            show('상속 설정 변경 실패', 'error', 500)
        }
    }

    const safeChildCount = Number(orgDetail?.childrenCount ?? orgDetail?.childCount ?? 0) || 0
    const safeUserCount = Number(orgDetail?.userCount ?? 0) || 0

    const isInsurer = (orgDetail?.type === 'INSURER')
    const isGA = (orgDetail?.type === 'GA')
    const isOperator = Array.isArray(user?.roles) && user.roles.includes(Roles.OPERATOR)
    const orgManagerRoles = [
        Roles.GA_MASTER_ADMIN,
        Roles.GA_MASTER_DEPUTY_ADMIN,
        Roles.GA_LEADER,
        Roles.GA_DEPUTY_LEADER,
        Roles.GA_BRANCH_MANAGER,
        Roles.GA_DEPUTY_BRANCH_MANAGER,
        Roles.GA_OTHER_ADMIN,
        Roles.GA_MANAGER,
        Roles.GA_OPERATION_MANAGER,
        Roles.INSURER_MASTER_ADMIN,
        Roles.INSURER_MASTER_DEPUTY_ADMIN,
        Roles.INSURER_LEADER,
        Roles.INSURER_DEPUTY_LEADER,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER,
        Roles.INSURER_OTHER_ADMIN,
        Roles.INSURER_MANAGER,
        Roles.INSURER_OPERATION_MANAGER,
        Roles.INSURER_EDUCATION_MANAGER,
    ]
    const hasOrgManagePermission = userHasAnyPermission(user, [Permissions.ORG_MANAGE])
    const isOrgManager = hasOrgManagePermission || (
        Array.isArray(user?.roles) && user.roles.some(r => orgManagerRoles.includes(r))
    )
    const inheritEnabled = !!mappings?.inheritEnabled
    const directInsurers = Array.isArray(mappings?.directInsurers) ? mappings.directInsurers : []
    const inheritedInsurers = Array.isArray(mappings?.inheritedInsurers) ? mappings.inheritedInsurers : []
    const overriddenInsurers = Array.isArray(mappings?.overriddenInsurers) ? mappings.overriddenInsurers : []

    // 조직 ID → 조직 객체 맵
    const orgById = useMemo(() => {
        const map = new Map()
        for (const o of orgOptions) {
            map.set(o.id, o)
        }
        return map
    }, [orgOptions])

    const deleteAuthorityOrgIds = useMemo(() => {
        if (!orgDetail?.id) return new Set()
        if (!isOperator && orgOptions.length === 0 && orgDetail?.parentOrgId != null) return new Set()

        const chain = []
        const seen = new Set()
        let current = orgDetail.id
        let guard = 0

        const parentIdOf = (id) => {
            const mapped = orgById.get(id) || orgById.get(Number(id))
            if (mapped) return mapped.parentOrgId ?? null
            if (String(id) === String(orgDetail.id)) return orgDetail.parentOrgId ?? null
            return null
        }

        while (current != null && guard < 100 && !seen.has(String(current))) {
            chain.push(current)
            seen.add(String(current))
            current = parentIdOf(current)
            guard += 1
        }

        const ids = new Set()
        if (chain.length === 1) {
            ids.add(String(chain[0]))
            return ids
        }
        for (let i = 1; i < chain.length - 1; i += 1) {
            ids.add(String(chain[i]))
        }
        ids.add(String(chain[chain.length - 2]))
        return ids
    }, [isOperator, orgById, orgDetail?.id, orgDetail?.parentOrgId, orgOptions.length])

    const canDeleteSelectedOrg = useMemo(() => {
        if (!orgDetail?.id) return false
        if (safeChildCount > 0 || safeUserCount > 0) return false
        if (isOperator) return true
        if (!isOrgManager || user?.orgId == null) return false
        return deleteAuthorityOrgIds.has(String(user.orgId))
    }, [deleteAuthorityOrgIds, isOperator, isOrgManager, orgDetail?.id, safeChildCount, safeUserCount, user?.orgId])

    const parentIsGaRoot = useMemo(() => {
        if (!isGA) return false
        const pid = orgDetail?.parentOrgId
        if (!pid) return false
        const parent = orgById.get(Number(pid))
        if (!parent) return false
        return parent.type === 'GA' && (parent.parentOrgId == null || parent.parentOrgId === '')
    }, [isGA, orgDetail?.parentOrgId, orgById])

    const lifeRootIds = useMemo(
        () =>
            orgOptions
                .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('생명') || o.name.includes('생보')))
                .map(o => o.id),
        [orgOptions]
    )

    const nonLifeRootIds = useMemo(
        () =>
            orgOptions
                .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('손해') || o.name.includes('화재') || o.name.includes('해상')))
                .map(o => o.id),
        [orgOptions]
    )

    // 조직 카테고리 판단: GA, INS_LIFE(생보), INS_NONLIFE(손보)
    const getOrgCategory = (orgId) => {
        const start = orgById.get(orgId)
        if (!start) return null
        if (start.type === 'GA') return 'GA'

        // 보험사 조직: 루트까지 올라가서 생보/손보 구분
        let current = start
        let root = start
        let safety = 0
        while (current && safety < 10) {
            root = current
            current = current.parentOrgId != null ? orgById.get(current.parentOrgId) : null
            safety += 1
        }
        if (lifeRootIds.includes(root.id)) return 'INS_LIFE'
        if (nonLifeRootIds.includes(root.id)) return 'INS_NONLIFE'
        return null
    }

    // 현재 매핑된 원수사의 경로(구분 / 1차 / 2차)를 계산
    const getInsurerPath = (orgId, fallbackName) => {
        const start = orgById.get(orgId)
        if (!start) return fallbackName || String(orgId)

        const segments = []
        let current = start
        let safety = 0
        while (current && safety < 5 && segments.length < 3) {
            segments.unshift(current.name)
            current = current.parentOrgId != null ? orgById.get(current.parentOrgId) : null
            safety += 1
        }
        return segments.join(' / ')
    }

    // 회사(orgId) 기준으로 "회사 / 1차 / 2차" 형태의 경로를 계산
    const getCompanyMappingPath = (companyOrgId, mappedOrgId, fallbackCompanyName) => {
        if (!companyOrgId) return fallbackCompanyName || ''
        if (!mappedOrgId) return (fallbackCompanyName || orgById.get(companyOrgId)?.name || '') + ' / 매핑정보 없음'

        const company = orgById.get(companyOrgId)
        const companyName = company?.name || fallbackCompanyName || String(companyOrgId)

        // mappedOrg → ... → companyOrg까지의 체인을 찾아서 company / child1 / child2만 반환
        let current = orgById.get(mappedOrgId)
        const chain = []
        let safety = 0
        while (current && safety < 20) {
            chain.unshift(current)
            if (String(current.id) === String(companyOrgId)) break
            current = current.parentOrgId != null ? orgById.get(current.parentOrgId) : null
            safety += 1
        }

        if (chain.length === 0 || String(chain[0].id) !== String(companyOrgId)) {
            // 예상한 회사 트리 밖의 조직이면 fallback
            return companyName + ' / ' + getInsurerPath(mappedOrgId, '')
        }

        const segs = [companyName]
        if (chain[1]?.name) segs.push(chain[1].name)
        if (chain[2]?.name) segs.push(chain[2].name)
        return segs.join(' / ')
    }

    const deriveCompanyL1L2 = (companyOrgId, mappedOrgId) => {
        if (!companyOrgId || !mappedOrgId) return { l1Id: '', l2Id: '' }
        let current = orgById.get(mappedOrgId)
        const chain = []
        let safety = 0
        while (current && safety < 20) {
            chain.unshift(current)
            if (String(current.id) === String(companyOrgId)) break
            current = current.parentOrgId != null ? orgById.get(current.parentOrgId) : null
            safety += 1
        }
        if (chain.length === 0 || String(chain[0].id) !== String(companyOrgId)) return { l1Id: '', l2Id: '' }
        return {
            l1Id: chain[1]?.id ? String(chain[1].id) : '',
            l2Id: chain[2]?.id ? String(chain[2].id) : '',
        }
    }

    // 선택된 조직의 카테고리 (GA / 생보 / 손보)
    const selectedOrgCategory = (() => {
        if (!orgDetail) return null
        if (orgDetail.type === 'GA') return 'GA'
        if (orgDetail.type === 'INSURER') return getOrgCategory(orgDetail.id)
        return null
    })()

    // GA 조직에서 "effective" 원수사를 생보/손보로 구분 (기존 호환: mappings.insurers는 effective로 내려옴)
    const effectiveInsurers = (mappings.insurers || [])

    const effectiveByMasterId = useMemo(() => {
        const map = new Map()
        for (const e of effectiveInsurers) {
            if (e?.insurerMasterId == null) continue
            map.set(String(e.insurerMasterId), e)
        }
        return map
    }, [effectiveInsurers])

    const directByMasterId = useMemo(() => {
        const map = new Map()
        for (const d of directInsurers) {
            if (d?.insurerMasterId == null) continue
            map.set(String(d.insurerMasterId), d)
        }
        return map
    }, [directInsurers])

    const overriddenMasterIds = useMemo(() => {
        const set = new Set()
        for (const o of overriddenInsurers) {
            if (o?.insurerMasterId == null) continue
            set.add(String(o.insurerMasterId))
        }
        return set
    }, [overriddenInsurers])

    const insurerMastersLife = useMemo(() => {
        return (insurerMasters || [])
            .filter(i => i?.orgId && getOrgCategory(i.orgId) === 'INS_LIFE')
            .sort((a, b) => {
                const an = String(a?.displayName || a?.orgName || a?.name || a?.code || '')
                const bn = String(b?.displayName || b?.orgName || b?.name || b?.code || '')
                return an.localeCompare(bn)
            })
    }, [insurerMasters, orgOptions, lifeRootIds, nonLifeRootIds])

    const insurerMastersNonLife = useMemo(() => {
        return (insurerMasters || [])
            .filter(i => i?.orgId && getOrgCategory(i.orgId) === 'INS_NONLIFE')
            .sort((a, b) => {
                const an = String(a?.displayName || a?.orgName || a?.name || a?.code || '')
                const bn = String(b?.displayName || b?.orgName || b?.name || b?.code || '')
                return an.localeCompare(bn)
            })
    }, [insurerMasters, orgOptions, lifeRootIds, nonLifeRootIds])

    if (!selectedOrg) {
        return (<div className="text-slate-500 text-sm">조직을 선택해주세요</div>)
    }

    if (loading) return <Loading />
    if (error) return <InlineError message={error} />
    if (!orgDetail) return <div className="text-sm text-slate-600">조직 정보를 찾을 수 없습니다</div>

    return (
        <div className="space-y-4">
            {activeTab === 'info' && (
                <>
            {/* 조직 정보 표시 */}
            <div className="rounded border p-3 text-sm">
                {/* <div className="font-medium">
                    {isGA ? '조직상세(GA)' : '조직상세'}
                </div> */}
                <div className="mt-2 space-y-1">
                    <div>이름 : {orgDetail.name} (ID:{orgDetail.id})</div>
                    <div>유형 : {orgDetail.type === 'GA (일반 대리점)' ? 'GA' : (orgDetail.type || '-')}</div>
                    <div>상위 조직 : {orgDetail.parentName || '없음'}</div>
                    <div>
                        생성일 : {orgDetail.createdAt ? new Date(orgDetail.createdAt).toISOString().slice(0, 10).replace(/-/g, '.') : '-'}
                    </div>
                    <div>하위 조직 수 : {safeChildCount}개</div>
                    <div>전체 인원수(하위 포함) : {safeUserCount}명</div>
                </div>
            </div>

            <div className="rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                조직홈페이지 관리는 이제 <span className="font-semibold">영업지원 &gt; 조직홈페이지</span> 메뉴에서 진행합니다.
            </div>

            {/* 조직 정보 수정 & 조직 이동 (한 줄 구성) */}
            <div className="space-y-3">
                {/* 조직 수정 */}
                <form onSubmit={handleUpdate} className="space-y-1">
                    <div className="font-medium">조직 이름 수정</div>
                    <div className="flex flex-col gap-2">
                        <input
                            className="flex-1 rounded border px-3 py-2"
                            placeholder="조직명"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            required
                        />
                        {isOperator && (
                            <select
                                className="flex-1 rounded border px-3 py-2"
                                value={editServiceProfile}
                                onChange={(e) => setEditServiceProfile(e.target.value)}
                                aria-label="서비스 프로필"
                            >
                                <option value="FULL_SERVICE">FULL_SERVICE</option>
                                <option value="COMMUNICATION_ONLY">COMMUNICATION_ONLY</option>
                            </select>
                        )}
                        <div className="flex justify-end">
                            <button type="submit" className="rounded border px-3 py-2 text-sm whitespace-nowrap">수정</button>
                        </div>
                    </div>
                </form>

                {/* 조직 이동 - 운영자 전용 */}
                {isOperator && (
                    <form onSubmit={handleMove} className="space-y-1">
                        <div className="font-medium">조직 이동 (운영자 전용)</div>
                        <div className="flex gap-2">
                            <select
                                className="flex-1 rounded border px-3 py-2"
                                value={editParent}
                                onChange={(e) => setEditParent(e.target.value)}
                            >
                                <option value="">상위 조직 없음</option>
                                {parentOptions
                                    .filter((option) => {
                                        if (!selectedOrgCategory) return true
                                        const full = orgById.get(option.id)
                                        if (!full) return false
                                        const parentCategory = getOrgCategory(option.id)
                                        if (selectedOrgCategory === 'GA') {
                                            return parentCategory === 'GA'
                                        }
                                        if (selectedOrgCategory === 'INS_LIFE' || selectedOrgCategory === 'INS_NONLIFE') {
                                            return parentCategory === selectedOrgCategory
                                        }
                                        return true
                                    })
                                    .map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name} ({option.type})
                                        </option>
                                    ))}
                            </select>
                            <button
                                type="submit"
                                className="rounded border px-3 py-2 text-sm whitespace-nowrap"
                                disabled={moveLoading}
                            >
                                {moveLoading ? '이동 중...' : '이동'}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {isInsurer && isOperator && (
                <div className="rounded border border-amber-100 bg-amber-50/60 p-4 space-y-3">
                    <div className="font-medium text-slate-900">청구 UI 브랜드 로고</div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                        보험금 청구·청구서식 등에서 보험사 목록에 쓰는 로고입니다. 상표·저작권 사용 권한을 확보한 이미지만 등록하세요.
                        여기서 지정한 로고가 조직 홈(랜딩) 로고보다 우선합니다.
                    </p>
                    <div className="flex flex-wrap items-start gap-4">
                        {orgDetail?.claimBrandLogoFileId ? (
                            <img
                                alt="청구용 로고"
                                className="h-14 w-14 shrink-0 rounded border bg-white object-contain p-1"
                                src={absoluteApiFilePreviewUrl(orgDetail.claimBrandLogoFileId)}
                            />
                        ) : (
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded border border-dashed border-slate-300 bg-white text-[11px] text-slate-400">
                                미등록
                            </div>
                        )}
                        <div className="flex min-w-0 flex-col gap-2">
                            <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50">
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                    className="hidden"
                                    disabled={claimLogoBusy}
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0]
                                        e.target.value = ''
                                        if (!file || !selectedOrg) return
                                        if (file.size > 10 * 1024 * 1024) {
                                            show('이미지는 10MB 이하여야 합니다.', 'error')
                                            return
                                        }
                                        setClaimLogoBusy(true)
                                        try {
                                            const body = new FormData()
                                            body.append('file', file)
                                            const res = await api.post('/files/upload/claim-brand-logo-image', body)
                                            const fileId = res?.data?.data?.fileId || res?.data?.fileId || ''
                                            if (!fileId) {
                                                show('파일 ID를 받지 못했습니다.', 'error')
                                                return
                                            }
                                            await api.patch(`/orgs/${selectedOrg.id}/claim-brand-logo`, { claimBrandLogoFileId: fileId })
                                            show('청구용 로고를 저장했습니다.', 'success')
                                            const detailRes = await api.get(`/orgs/${selectedOrg.id}`)
                                            const payload = (detailRes?.data && detailRes.data.data !== undefined) ? detailRes.data.data : detailRes.data
                                            setOrgDetail(payload)
                                            onUpdate?.()
                                        } catch (err) {
                                            show(err?.response?.data?.message || err?.message || '저장 실패', 'error')
                                        } finally {
                                            setClaimLogoBusy(false)
                                        }
                                    }}
                                />
                                {claimLogoBusy ? '처리 중…' : '이미지 업로드'}
                            </label>
                            {orgDetail?.claimBrandLogoFileId ? (
                                <button
                                    type="button"
                                    className="w-fit text-left text-xs text-rose-700 underline hover:text-rose-900 disabled:opacity-50"
                                    disabled={claimLogoBusy}
                                    onClick={async () => {
                                        if (!selectedOrg) return
                                        if (!window.confirm('청구용 로고를 해제할까요? (랜딩 로고 등으로 폴백)')) return
                                        setClaimLogoBusy(true)
                                        try {
                                            await api.patch(`/orgs/${selectedOrg.id}/claim-brand-logo`, { claimBrandLogoFileId: null })
                                            show('해제했습니다.', 'success')
                                            const detailRes = await api.get(`/orgs/${selectedOrg.id}`)
                                            const payload = (detailRes?.data && detailRes.data.data !== undefined) ? detailRes.data.data : detailRes.data
                                            setOrgDetail(payload)
                                            onUpdate?.()
                                        } catch (err) {
                                            show(err?.response?.data?.message || err?.message || '실패', 'error')
                                        } finally {
                                            setClaimLogoBusy(false)
                                        }
                                    }}
                                >
                                    로고 해제
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* 원수사-GA 매핑 - 추후 추가될 기능이므로 현재는 히든 처리 */}
            {false && (
            <div className="rounded border p-3">
                <div className="font-medium mb-2">원수사-GA 매핑</div>
                {isInsurer && isOperator && (
                    <div className="space-y-2">
                        <div className="text-sm text-slate-600">현재 매핑된 GA</div>
                        <ul className="list-disc pl-5 text-sm">
                            {(mappings.gas || []).map(g => (
                                <li key={g.id} className="flex items-center justify-between">
                                    <span>{g.name}</span>
                                    <button className="text-red-600 text-xs" onClick={() => onRemoveMapping(g.id)}>해제</button>
                                </li>
                            ))}
                            {(mappings.gas || []).length === 0 && <li className="text-slate-500">없음</li>}
                        </ul>
                        <div className="mt-2">
                            {/* 후보는 GA만 표시 */}
                            <OrgMultiSelect value={candidateIds} onChange={setCandidateIds} title="추가할 GA 선택" filterType="GA" />
                            <button type="button" className="mt-2 rounded border px-3 py-2 text-sm" onClick={onAddMapping}>매핑 추가</button>
                        </div>
                    </div>
                )}
                {isGA && (
                    <div className="space-y-2">
                        {/* 상위(사업단) 매핑 사용 토글: 하위 조직에서만 */}
                        {orgDetail?.parentOrgId && !parentIsGaRoot && (
                            <div className="rounded border bg-slate-50 p-3 text-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-medium">상위(사업단) 매핑 사용</div>
                                        <div className="text-xs text-slate-600 mt-0.5">
                                            ON이면 상위 조직의 원수사 매핑을 상속(읽기 전용)으로 사용합니다. 동일 보험사(회사)로 직접 매핑을 추가하면 상속 매핑을 override 합니다.
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 pt-0.5">
                                        <label className="inline-flex items-center gap-1.5 text-xs">
                                            <input
                                                type="radio"
                                                name="inherit_parent_mappings"
                                                checked={inheritEnabled}
                                                onChange={() => onToggleInherit(true)}
                                            />
                                            <span>ON</span>
                                        </label>
                                        <label className="inline-flex items-center gap-1.5 text-xs">
                                            <input
                                                type="radio"
                                                name="inherit_parent_mappings"
                                                checked={!inheritEnabled}
                                                onChange={() => onToggleInherit(false)}
                                            />
                                            <span>OFF</span>
                                        </label>
                                    </div>
                                </div>
                                {inheritEnabled && inheritedInsurers.length > 0 && (
                                    <div className="mt-2 text-xs text-slate-600">
                                        상속 매핑 {inheritedInsurers.length}건이 적용됩니다.
                                    </div>
                                )}
                            </div>
                        )}
                        {orgDetail?.parentOrgId && parentIsGaRoot && (
                            <div className="rounded border bg-slate-50 p-3 text-sm">
                                <div className="font-medium">상위(사업단) 매핑 사용</div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                    이 조직은 GA 루트(회사) 바로 하위(최상위 사업단) 조직이므로 상위 매핑 상속 기능을 사용할 수 없습니다.
                                </div>
                            </div>
                        )}

                        {/* 보험사(회사) 전체 리스트 기반 매핑 현황 (생명보험 / 손해보험 구분) */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-sm text-slate-600">
                                    보험사(회사) 전체 목록 기준 매핑 현황
                                    <span className="ml-2 text-xs text-slate-500">
                                        (직접 매핑은 {directInsurers.length}건)
                                    </span>
                                </div>
                                {inheritEnabled && overriddenInsurers.length > 0 && (
                                    <div className="text-xs text-amber-700">
                                        상속 {overriddenInsurers.length}건이 직접 매핑으로 override 됨
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                {/* 생명보험 */}
                                <div>
                                    <div className="font-medium mb-1">
                                        생명보험
                                        <span className="ml-2 text-xs text-slate-500">
                                            ({insurerMastersLife.length}개 회사)
                                        </span>
                                    </div>
                                    <div className="border rounded p-2 max-h-80 overflow-y-auto space-y-1">
                                        {insurerMastersLife.length > 0 ? (
                                            insurerMastersLife.map((insurer) => {
                                                const mid = String(insurer.id)
                                                const effective = effectiveByMasterId.get(mid)
                                                const direct = directByMasterId.get(mid)
                                                const mappedOrgId = effective?.id
                                                const isInherited = effective?.source === 'INHERITED'
                                                const isOverride = overriddenMasterIds.has(mid) && !!direct
                                                const displayName = insurer?.displayName || insurer?.orgName || insurer?.name || insurer?.code
                                                const pathText = getCompanyMappingPath(insurer.orgId, mappedOrgId, displayName)

                                                return (
                                                    <div key={mid} className="flex items-start justify-between gap-2 rounded px-2 py-1 bg-slate-50">
                                                        <div className="text-xs leading-snug">
                                                            <div className="font-medium">
                                                                {pathText}
                                                            </div>
                                                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                                                {!mappedOrgId && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-slate-500 bg-white">
                                                                        미매핑
                                                                    </span>
                                                                )}
                                                                {mappedOrgId && !isInherited && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-emerald-700 bg-emerald-50 border-emerald-200">
                                                                        직접
                                                                    </span>
                                                                )}
                                                                {mappedOrgId && isInherited && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-slate-600 bg-white">
                                                                        상속
                                                                    </span>
                                                                )}
                                                                {isOverride && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-amber-800 bg-amber-50 border-amber-200">
                                                                        override
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {isInherited && (
                                                                <div className="mt-0.5 text-[11px] text-slate-500">
                                                                    상속됨: {effective?.sourceOrgName || `조직 ${effective?.sourceOrgId}`}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 pt-0.5">
                                                            <button
                                                                type="button"
                                                                className="text-[11px] whitespace-nowrap underline"
                                                                onClick={() => {
                                                                    setEditInsurerMaster(insurer)
                                                                    const preset = deriveCompanyL1L2(insurer.orgId, mappedOrgId)
                                                                    setEditL1(preset.l1Id)
                                                                    setEditL2(preset.l2Id)
                                                                }}
                                                            >
                                                                수정
                                                            </button>
                                                            {direct?.id && (
                                                                <button
                                                                    type="button"
                                                                    className="text-red-600 text-[11px] whitespace-nowrap underline"
                                                                    onClick={() => onRemoveMapping(direct.id)}
                                                                >
                                                                    해제
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <div className="text-xs text-slate-500">보험사(생보) 목록을 불러오는 중이거나 없습니다</div>
                                        )}
                                    </div>
                                </div>

                                {/* 손해보험 */}
                                <div>
                                    <div className="font-medium mb-1">
                                        손해보험
                                        <span className="ml-2 text-xs text-slate-500">
                                            ({insurerMastersNonLife.length}개 회사)
                                        </span>
                                    </div>
                                    <div className="border rounded p-2 max-h-80 overflow-y-auto space-y-1">
                                        {insurerMastersNonLife.length > 0 ? (
                                            insurerMastersNonLife.map((insurer) => {
                                                const mid = String(insurer.id)
                                                const effective = effectiveByMasterId.get(mid)
                                                const direct = directByMasterId.get(mid)
                                                const mappedOrgId = effective?.id
                                                const isInherited = effective?.source === 'INHERITED'
                                                const isOverride = overriddenMasterIds.has(mid) && !!direct
                                                const displayName = insurer?.displayName || insurer?.orgName || insurer?.name || insurer?.code
                                                const pathText = getCompanyMappingPath(insurer.orgId, mappedOrgId, displayName)

                                                return (
                                                    <div key={mid} className="flex items-start justify-between gap-2 rounded px-2 py-1 bg-slate-50">
                                                        <div className="text-xs leading-snug">
                                                            <div className="font-medium">
                                                                {pathText}
                                                            </div>
                                                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                                                {!mappedOrgId && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-slate-500 bg-white">
                                                                        미매핑
                                                                    </span>
                                                                )}
                                                                {mappedOrgId && !isInherited && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-emerald-700 bg-emerald-50 border-emerald-200">
                                                                        직접
                                                                    </span>
                                                                )}
                                                                {mappedOrgId && isInherited && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-slate-600 bg-white">
                                                                        상속
                                                                    </span>
                                                                )}
                                                                {isOverride && (
                                                                    <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] text-amber-800 bg-amber-50 border-amber-200">
                                                                        override
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {isInherited && (
                                                                <div className="mt-0.5 text-[11px] text-slate-500">
                                                                    상속됨: {effective?.sourceOrgName || `조직 ${effective?.sourceOrgId}`}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 pt-0.5">
                                                            <button
                                                                type="button"
                                                                className="text-[11px] whitespace-nowrap underline"
                                                                onClick={() => {
                                                                    setEditInsurerMaster(insurer)
                                                                    const preset = deriveCompanyL1L2(insurer.orgId, mappedOrgId)
                                                                    setEditL1(preset.l1Id)
                                                                    setEditL2(preset.l2Id)
                                                                }}
                                                            >
                                                                수정
                                                            </button>
                                                            {direct?.id && (
                                                                <button
                                                                    type="button"
                                                                    className="text-red-600 text-[11px] whitespace-nowrap underline"
                                                                    onClick={() => onRemoveMapping(direct.id)}
                                                                >
                                                                    해제
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <div className="text-xs text-slate-500">보험사(손보) 목록을 불러오는 중이거나 없습니다</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* GA 매핑 편집 팝업 */}
            {isGA && !!editInsurerMaster && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-lg rounded bg-white p-4 shadow">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-medium">매핑 수정</div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                    {editInsurerMaster?.name || '보험사'} · 1차/2차 하위 조직을 선택하세요
                                </div>
                            </div>
                            <button
                                type="button"
                                className="text-sm text-slate-600"
                                onClick={() => {
                                    setEditInsurerMaster(null)
                                    setEditL1('')
                                    setEditL2('')
                                }}
                            >
                                닫기
                            </button>
                        </div>

                        {(() => {
                            const mid = String(editInsurerMaster.id)
                            const effective = effectiveByMasterId.get(mid)
                            const direct = directByMasterId.get(mid)
                            const mappedOrgId = effective?.id
                            const companyOrgId = editInsurerMaster?.orgId
                            const l1Options = orgOptions.filter(o => String(o.parentOrgId) === String(companyOrgId))
                            const l2Options = editL1 ? orgOptions.filter(o => String(o.parentOrgId) === String(editL1)) : []

                            const companyName = editInsurerMaster?.displayName || editInsurerMaster?.orgName || editInsurerMaster?.name || editInsurerMaster?.code
                            const currentText = getCompanyMappingPath(companyOrgId, mappedOrgId, companyName)

                            return (
                                <div className="mt-3 space-y-3">
                                    <div className="rounded border bg-slate-50 p-2 text-xs text-slate-700">
                                        현재 적용: <span className="font-medium">{currentText}</span>
                                        {effective?.source === 'INHERITED' && (
                                            <div className="mt-1 text-[11px] text-slate-500">
                                                상속됨: {effective?.sourceOrgName || `조직 ${effective?.sourceOrgId}`}
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 gap-2">
                                        <select
                                            className="w-full rounded border px-3 py-2 text-sm"
                                            value={editL1}
                                            onChange={(e) => {
                                                setEditL1(e.target.value)
                                                setEditL2('')
                                            }}
                                        >
                                            <option value="">{l1Options.length > 0 ? '1차 하위 조직 선택' : '선택 가능한 1차 하위 조직이 없습니다'}</option>
                                            {l1Options.map(o => (
                                                <option key={o.id} value={o.id}>{o.name}</option>
                                            ))}
                                        </select>

                                        <select
                                            className="w-full rounded border px-3 py-2 text-sm"
                                            value={editL2}
                                            onChange={(e) => setEditL2(e.target.value)}
                                            disabled={!editL1 || l2Options.length === 0}
                                        >
                                            <option value="">{editL1 ? '2차 하위 조직 선택(선택)' : '먼저 1차 하위를 선택하세요'}</option>
                                            {l2Options.map(o => (
                                                <option key={o.id} value={o.id}>{o.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            className="rounded border px-3 py-2 text-sm"
                                            onClick={() => {
                                                setEditInsurerMaster(null)
                                                setEditL1('')
                                                setEditL2('')
                                            }}
                                            disabled={editSaving}
                                        >
                                            취소
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded border px-3 py-2 text-sm"
                                            disabled={editSaving}
                                            onClick={async () => {
                                                try {
                                                    // 정책: 가능한 경우(2차 하위 조직 옵션이 존재하는 경우) 2차까지 선택해야 한다.
                                                    // 1차까지만 매핑하면 "본부" 등 상위 단위로 매핑되어 타 지점(강남 등) 배정이 발생할 수 있다.
                                                    const requiresL2 = !!editL1 && Array.isArray(l2Options) && l2Options.length > 0
                                                    const targetId = requiresL2 ? editL2 : (editL2 || editL1)
                                                    if (!targetId) {
                                                        show(requiresL2 ? '2차 하위 조직을 선택하세요.' : '1차 또는 2차 하위 조직을 선택하세요.', 'error', 500)
                                                        return
                                                    }

                                                    // 상속으로 이미 동일하게 적용 중이라면 굳이 direct를 만들지 않음
                                                    if (!direct?.id && effective?.source === 'INHERITED' && String(effective?.id) === String(targetId)) {
                                                        show('이미 상속 매핑으로 동일하게 적용 중입니다.', 'info', 500)
                                                        setEditInsurerMaster(null)
                                                        setEditL1('')
                                                        setEditL2('')
                                                        return
                                                    }

                                                    setEditSaving(true)

                                                    // direct가 있으면 먼저 삭제 후 추가(회사별 1:1 제약)
                                                    if (direct?.id && String(direct.id) !== String(targetId)) {
                                                        await api.delete(`/orgs/${selectedOrg.id}/mappings`, { params: { counterpartOrgId: direct.id } })
                                                    }

                                                    // direct가 없거나, 다른 값으로 변경하는 경우에만 추가
                                                    if (!direct?.id || String(direct?.id) !== String(targetId)) {
                                                        await api.post(`/orgs/${selectedOrg.id}/mappings`, { counterpartOrgId: Number(targetId) })
                                                    }

                                                    show('매핑이 저장되었습니다', 'success' , 500)
                                                    await reloadMappings()
                                                    setEditInsurerMaster(null)
                                                    setEditL1('')
                                                    setEditL2('')
                                                } catch (e) {
                                                    show('매핑 저장 실패', 'error')
                                                } finally {
                                                    setEditSaving(false)
                                                }
                                            }}
                                        >
                                            {editSaving ? '저장 중...' : '저장'}
                                        </button>
                                    </div>
                                </div>
                            )
                        })()}
                    </div>
                </div>
            )}

            {/* 조직 삭제 (위험 작업) */}
            {(() => {
                if (!canDeleteSelectedOrg) return null
                return (
                    <form onSubmit={handleDelete} className="space-y-2">
                        <div className="font-medium text-red-600">위험 작업</div>
                        <button
                            type="submit"
                            className="rounded border border-red-300 text-red-600 px-3 py-2 text-sm hover:bg-red-50"
                        >
                            삭제
                        </button>
                    </form>
                )
            })()}
                </>
            )}
        </div>
    )
}

// 조직 홈페이지 탭 컴포넌트 (뷰어 모드)
function OrgLandingPageTab({ orgId, orgName }) {
    const navigate = useNavigate()
    const [landingPage, setLandingPage] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!orgId) return
        loadLandingPage()
    }, [orgId])

    const loadLandingPage = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api.get(`/orgs/${orgId}/landing-page`)
            const data = res?.data?.data || res?.data
            setLandingPage(data)
        } catch (e) {
            if (e.response?.status === 404) {
                // 아직 생성되지 않은 경우
                setLandingPage(null)
            } else {
                setError('홈페이지 정보를 불러오지 못했습니다')
            }
        } finally {
            setLoading(false)
        }
    }

    if (loading) return <div className="text-center py-8">로딩 중...</div>
    if (error) return <div className="text-center py-8 text-red-600">{error}</div>

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-semibold">조직 홈페이지</h3>
                    <p className="text-sm text-slate-600 mt-1">
                        {landingPage?.isPublished ? (
                            <span className="text-green-600">공개됨</span>
                        ) : landingPage ? (
                            <span className="text-slate-500">초안</span>
                        ) : (
                            <span className="text-slate-400">미생성</span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    {landingPage && (
                        <a
                            href={`/orgs/${orgId}/landing`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                        >
                            공개 페이지 보기
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={() => window.open(`/orgs/${orgId}/landing/edit`, '_blank', 'noopener,noreferrer')}
                        className="rounded border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100"
                    >
                        수정하기
                    </button>
                </div>
            </div>

            {landingPage ? (
                <div className="rounded border overflow-hidden">
                    <OrgLandingPageViewer
                        landingPageData={landingPage}
                        orgName={orgName}
                    />
                </div>
            ) : (
                <div className="rounded border p-8 text-center bg-slate-50">
                    <p className="text-slate-600 mb-4">아직 홈페이지가 생성되지 않았습니다.</p>
                    <button
                        type="button"
                        onClick={() => window.open(`/orgs/${orgId}/landing/edit`, '_blank', 'noopener,noreferrer')}
                        className="rounded border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100"
                    >
                        홈페이지 만들기
                    </button>
                </div>
            )}
        </div>
    )
}
